import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Plus, Link2, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { adminAPI } from '../services/api';

const Row = ({ item, onDone }) => {
  const [target, setTarget] = useState(item.suggestions[0] || '');
  const [busy, setBusy] = useState('');
  const key = `${item.brand}-${item.model}`;

  const run = async (label, fn) => {
    setBusy(label);
    try { const r = await fn(); toast.success(r.message); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Opération impossible'); }
    finally { setBusy(''); }
  };

  return (
    <div className="p-3 flex flex-wrap items-start gap-3" data-testid={`compat-unmatched-${key}`}>
      <div className="flex-1 min-w-[240px]">
        <p className="text-sm font-semibold text-gray-900">{item.brand} <span className="font-normal">{item.model}</span> {item.model_years && <span className="text-xs text-gray-500">{item.model_years}</span>}</p>
        <p className="text-xs text-gray-500 mt-0.5">{item.product_count} produit(s) : {item.products.map(p => p.ref).join(', ')}{item.product_count > item.products.length ? '…' : ''}</p>
        {item.engines.length > 0 && (
          <ul className="mt-1 flex flex-wrap gap-1">{item.engines.map((e, i) => <li key={i} className="text-[11px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{e.engine}{e.engine_years ? <span className="text-gray-400"> · {e.engine_years}</span> : null}</li>)}</ul>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {item.suggestions.length > 0 && (
          <>
            <select value={target} onChange={(e) => setTarget(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white max-w-[220px]" data-testid={`compat-unmatched-target-${key}`}>
              {item.suggestions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <Button type="button" size="sm" variant="outline" disabled={!!busy || !target} onClick={() => run('link', () => adminAPI.compatLink(item.brand, item.model, target))} data-testid={`compat-unmatched-link-${key}`}>
              {busy === 'link' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Link2 size={14} className="mr-1" />} Associer
            </Button>
          </>
        )}
        <Button type="button" size="sm" className="bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white" disabled={!!busy} onClick={() => run('create', () => adminAPI.compatCreateVehicle({ brand: item.brand, model: item.model, model_years: item.model_years, engines: item.engines }))} data-testid={`compat-unmatched-create-${key}`}>
          {busy === 'create' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />} Créer le véhicule
        </Button>
        <button type="button" className="p-1.5 text-gray-400 hover:text-red-600" title="Retirer ces lignes des produits" disabled={!!busy} onClick={() => window.confirm(`Retirer « ${item.brand} ${item.model} » de ${item.product_count} produit(s) ?`) && run('delete', () => adminAPI.compatDeleteUnmatched(item.brand, item.model))} data-testid={`compat-unmatched-delete-${key}`}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

export const UnmatchedFitmentsPanel = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    adminAPI.compatUnmatched().then(setData).catch(() => setData({ total: 0, items: [] })).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const done = () => { load(); onChanged?.(); };

  return (
    <div className="bg-white rounded-lg shadow-sm mb-6" data-testid="compat-unmatched-panel">
      <div className="p-4 border-b bg-amber-50 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><AlertTriangle size={16} className="text-amber-600" /> Modèles collés non reconnus {data && <span className="text-xs font-normal text-gray-500" data-testid="compat-unmatched-count">({data.total})</span>}</h2>
          <p className="text-xs text-gray-600 mt-0.5">Véhicules collés sur des fiches produit mais absents de la base : créez-les en un clic (motorisations reprises) ou associez-les à un modèle existant pour activer le filtre par véhicule / plaque.</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading} data-testid="compat-unmatched-refresh"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /></Button>
      </div>
      {!data ? <div className="p-4"><Loader2 size={18} className="animate-spin text-orange-500" /></div>
        : data.total === 0 ? <p className="p-4 text-sm text-gray-500" data-testid="compat-unmatched-empty">Tous les véhicules collés sont reconnus dans la base.</p>
        : <div className="divide-y max-h-[420px] overflow-y-auto">{data.items.map(it => <Row key={`${it.brand}|${it.model}`} item={it} onDone={done} />)}</div>}
    </div>
  );
};

export default UnmatchedFitmentsPanel;
